import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";

import { Typography } from "@/components/ui/typography";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/components/ui/utils";

// The worker ships with the app rather than from a CDN, so the code that parses
// someone else's PDF is the code we bundled and pinned (``pdfjs-dist`` at the
// version react-pdf is built against: 5.x, since 6 relies on
// ``Map.getOrInsertComputed``, which most browsers do not have yet).
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

/**
 * How a PDF is read. It is a file a third party made - a statement, a receipt
 * forwarded by email - so it is read as a picture of its pages and nothing
 * more: no annotation layer (see ``Page`` below), so none of its links, form
 * fields or actions reach the app; no code built from its fonts; no XFA forms;
 * and no credentials sent with the request. The whole file is fetched in one
 * go, which keeps what storage has to allow across origins to a plain GET.
 */
const PDF_OPTIONS = {
  // Never compile a font's glyphs into code: pdf.js falls back to drawing
  // them, so nothing in the file can be evaluated (CVE-2024-4367 was that path).
  isEvalSupported: false,
  enableXfa: false,
  disableRange: true,
  disableStream: true,
  withCredentials: false,
};

/**
 * Pages are drawn at a width rounded up to a step of this, and scaled to the
 * panel: a panel dragged a few pixels at a time is the same drawing, stretched,
 * rather than a fresh one per pixel.
 */
const DRAWN_WIDTH_STEP = 400;
/** How long a panel has to stop moving before a wider drawing is made. */
const REDRAW_AFTER_MS = 200;

/**
 * A PDF's pages, each fitted to the width of wherever it is shown — a document
 * panel, a hover over the file that was sent. Drawn by the app rather than
 * handed to the browser's own viewer, which is a frame we cannot see into and
 * will not draw a sandboxed file at all.
 *
 * Drawn once and scaled to fit: redrawn on every width a resize passed
 * through, each page went blank or to its placeholder - a different height -
 * and back, so dragging the panel flickered and everything below jumped. A
 * page is drawn again only once the panel has settled wider than it was drawn
 * for; narrower, the drawing it has is sharp enough.
 */
export function PdfPreview({
  url,
  highlight,
}: {
  /** Where the file is read from; none while it is still being made. */
  url: string | null;
  /**
   * What to mark where the file has it - a payment's amount and what it says,
   * on the statement it was read from - and scrolled to: patterns, run over
   * each page's text to find how the file writes it, and that text marked
   * wherever the page has it exactly.
   */
  highlight?: RegExp[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToHighlightRef = useRef(false);
  // Each page's marks: by the index of the text item they fall in, the
  // stretches of its text to mark.
  const [marks, setMarks] = useState<
    Record<number, Record<number, [number, number][]>>
  >({});
  // The page's text as the text layer draws it: escaped, since it is the
  // file's own words set as markup, and marked where a mark falls in it.
  const renderText = useCallback(
    ({
      str,
      pageIndex,
      itemIndex,
    }: {
      str: string;
      pageIndex: number;
      itemIndex: number;
    }) => {
      const escape = (text: string) =>
        text.replace(
          /[&<>"']/g,
          (character) => `&#${character.charCodeAt(0)};`,
        );
      let html = "";
      let at = 0;
      for (const [from, to] of marks[pageIndex]?.[itemIndex] ?? []) {
        html += `${escape(str.slice(at, from))}<mark class="pdf-preview-highlight">${escape(str.slice(from, to))}</mark>`;
        at = to;
      }
      return html + escape(str.slice(at));
    },
    [marks],
  );
  const [width, setWidth] = useState<number>();
  const [drawnWidth, setDrawnWidth] = useState<number>();
  const [pageCount, setPageCount] = useState(0);
  // Each page's width over its height, once read, so its box holds the page's
  // own shape whatever is being drawn inside it.
  const [shapes, setShapes] = useState<Record<number, number>>({});
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.floor(entry.contentRect.width)),
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (width === undefined) return;
    const wanted = Math.ceil(width / DRAWN_WIDTH_STEP) * DRAWN_WIDTH_STEP;
    if (drawnWidth === undefined) {
      setDrawnWidth(wanted);
      return;
    }
    if (wanted <= drawnWidth) return;
    const settled = setTimeout(() => setDrawnWidth(wanted), REDRAW_AFTER_MS);
    return () => clearTimeout(settled);
  }, [width, drawnWidth]);

  if (hasFailed) {
    return (
      <div className="flex flex-col items-center gap-3 p-12 text-center">
        <Typography level="body-sm" className="text-muted-foreground">
          This PDF could not be shown here.
        </Typography>
        <a
          href={url ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline-offset-4 hover:underline"
        >
          Open file
        </a>
      </div>
    );
  }

  const pageSkeleton = (
    <Skeleton
      variant="rectangular"
      className="w-full [aspect-ratio:8.5_/_11] rounded-sm"
    />
  );

  return (
    <div
      ref={containerRef}
      data-testid="document-pdf-preview"
      className={cn(
        "flex flex-col gap-3 p-3 w-full box-border",
        // Over the page's own drawing of the words, which show through.
        "[&_mark.pdf-preview-highlight]:bg-[rgba(255,196,0,0.45)] [&_mark.pdf-preview-highlight]:text-transparent [&_mark.pdf-preview-highlight]:rounded-[2px]",
      )}
    >
      <Document
        file={url}
        options={PDF_OPTIONS}
        loading={pageSkeleton}
        noData={pageSkeleton}
        error={null}
        onLoadSuccess={({ numPages }) => setPageCount(numPages)}
        onLoadError={() => setHasFailed(true)}
        onSourceError={() => setHasFailed(true)}
      >
        {width !== undefined &&
          drawnWidth !== undefined &&
          Array.from({ length: pageCount }, (_page, index) => (
            <div
              key={index}
              className="relative w-full aspect-(--aspect-ratio) overflow-hidden mb-3 shadow-sm bg-white last-of-type:mb-0"
              style={
                {
                  "--aspect-ratio": String(shapes[index] ?? 8.5 / 11),
                } as CSSProperties
              }
            >
              {/* The page and its text together, so selecting text still
                  lands on the words while it is stretched. */}
              <div
                className="w-(--drawn-width) origin-[top_left] transform-(--transform)"
                style={
                  {
                    "--drawn-width":
                      drawnWidth <= 1 && drawnWidth !== 0
                        ? `${drawnWidth * 100}%`
                        : `${drawnWidth}px`,
                    "--transform": `scale(${width / drawnWidth})`,
                  } as CSSProperties
                }
              >
                <Page
                  pageNumber={index + 1}
                  width={drawnWidth}
                  loading={pageSkeleton}
                  onLoadSuccess={(page) =>
                    setShapes((known) =>
                      known[index]
                        ? known
                        : {
                            ...known,
                            [index]: page.originalWidth / page.originalHeight,
                          },
                    )
                  }
                  // Links, form fields and actions in the file stay in the
                  // file: the page is its picture and its selectable text.
                  renderAnnotationLayer={false}
                  renderForms={false}
                  customTextRenderer={highlight ? renderText : undefined}
                  // Where the page has what is asked for: each pattern run
                  // over the page's text, as one, to find how the file writes
                  // it - then every place it has that text exactly, between
                  // words, marked in the items it spans.
                  onGetTextSuccess={({ items }) => {
                    if (!highlight?.length) return;
                    // Items run together, a space between: a cell is a word.
                    // Numbered as the text layer numbers them, marked content
                    // among them.
                    const pieces = items.map((item) =>
                      "str" in item ? item.str : "",
                    );
                    const starts: number[] = [];
                    let text = "";
                    for (const piece of pieces) {
                      starts.push(text.length);
                      text += piece ? `${piece} ` : "";
                    }
                    const found = new Set<string>();
                    for (const pattern of highlight) {
                      const flags = pattern.flags.includes("g")
                        ? pattern.flags
                        : `${pattern.flags}g`;
                      for (const match of text.matchAll(
                        new RegExp(pattern.source, flags),
                      )) {
                        if (match[0].trim()) found.add(match[0].trim());
                      }
                    }
                    const pageMarks: Record<number, [number, number][]> = {};
                    for (const exact of found) {
                      for (
                        let from = text.indexOf(exact);
                        from !== -1;
                        from = text.indexOf(exact, from + 1)
                      ) {
                        const to = from + exact.length;
                        // Whole, not part of a longer word or number.
                        if (
                          /[\w.,]/.test(text[from - 1] ?? "") ||
                          /[\w]/.test(text[to] ?? "")
                        ) {
                          continue;
                        }
                        starts.forEach((start, itemIndex) => {
                          const piece = pieces[itemIndex];
                          if (to <= start || from >= start + piece.length) {
                            return;
                          }
                          const markFrom = Math.max(from, start) - start;
                          const markTo =
                            Math.min(to, start + piece.length) - start;
                          // The space between two words is not marked.
                          if (!piece.slice(markFrom, markTo).trim()) return;
                          (pageMarks[itemIndex] ??= []).push([
                            markFrom,
                            markTo,
                          ]);
                        });
                      }
                    }
                    // In order, and none inside another.
                    for (const ranges of Object.values(pageMarks)) {
                      ranges.sort((a, b) => a[0] - b[0]);
                      for (let i = ranges.length - 1; i > 0; i--) {
                        if (ranges[i][0] < ranges[i - 1][1]) {
                          ranges[i - 1][1] = Math.max(
                            ranges[i - 1][1],
                            ranges[i][1],
                          );
                          ranges.splice(i, 1);
                        }
                      }
                    }
                    setMarks((known) => ({ ...known, [index]: pageMarks }));
                  }}
                  onRenderTextLayerSuccess={() => {
                    const mark =
                      containerRef.current?.querySelector<HTMLElement>(
                        "mark.pdf-preview-highlight",
                      );
                    if (!mark || hasScrolledToHighlightRef.current) return;
                    hasScrolledToHighlightRef.current = true;
                    mark.scrollIntoView({ block: "center" });
                  }}
                />
              </div>
            </div>
          ))}
      </Document>
    </div>
  );
}

/**
 * A PDF's first page, small: what a card shows of the file it stands for.
 * Drawn at twice its size and scaled down, so it stays sharp on a dense
 * screen; its picture alone, with no text to select. Unreadable, it is empty.
 */
export function PdfThumbnail({ url, width }: { url: string; width: number }) {
  const [hasFailed, setHasFailed] = useState(false);
  if (hasFailed) return null;
  // Loading, the page's own shape at the size it is drawn at.
  const pageSkeleton = (
    <Skeleton
      variant="rectangular"
      className="w-(--width) [aspect-ratio:8.5_/_11]"
      style={
        {
          "--width":
            width * 2 <= 1 && width * 2 !== 0
              ? `${width * 2 * 100}%`
              : `${width * 2}px`,
        } as CSSProperties
      }
    />
  );
  return (
    <div
      data-testid="pdf-thumbnail"
      className="w-(--width) overflow-hidden bg-white"
      style={
        {
          "--width":
            width <= 1 && width !== 0 ? `${width * 100}%` : `${width}px`,
        } as CSSProperties
      }
    >
      <div
        className="w-(--width) origin-[top_left] transform-[scale(0.5)]"
        style={
          {
            "--width":
              width * 2 <= 1 && width * 2 !== 0
                ? `${width * 2 * 100}%`
                : `${width * 2}px`,
          } as CSSProperties
        }
      >
        <Document
          file={url}
          options={PDF_OPTIONS}
          loading={pageSkeleton}
          error={null}
          onLoadError={() => setHasFailed(true)}
          onSourceError={() => setHasFailed(true)}
        >
          <Page
            pageNumber={1}
            width={width * 2}
            loading={pageSkeleton}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            renderForms={false}
          />
        </Document>
      </div>
    </div>
  );
}
